/**
 * Bulk Edit modal – center creative column (senders + texts side by side).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import { MessageSquare } from 'lucide-react';

import { cn } from '@/lib/utils';
import { AnimatePresence } from 'framer-motion';

import { AiGenerateDropdown } from '@/features/campaign-texts/components/AiGenerateDropdown';
import { AiGenerateSendersDropdown } from '@/features/campaign-texts/components/AiGenerateSendersDropdown';
import type { DropLabel } from '@/features/campaign-texts/components/PerDropGroupSelector';
import { GeneratedTextsPreview } from '@/features/campaign-texts/components/GeneratedTextsPreview';
import { GeneratedTextsPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedTextsPreviewPerDrop';
import { GeneratedSendersPreview } from '@/features/campaign-texts/components/GeneratedSendersPreview';
import { GeneratedSendersPreviewPerDrop } from '@/features/campaign-texts/components/GeneratedSendersPreviewPerDrop';
import { useAuth } from '@/providers/AuthProvider';
import { SendersPickerSection, TextsPickerSection } from '@/features/smswiser-campaigns/components/drop-modals';
import type { PerformanceTag, SenderTemplateOption } from '@/features/smswiser-campaigns/components/drop-modals/drop-modals.types';
import type { useBulkEditPreparedDropsForm } from '@/features/smswiser-campaigns/components/prepared-drop-modals/useBulkEditPreparedDropsForm';
import type { ScaleDropModalProps } from '../ScaleDropModal.types';
import { ColumnHeader } from '../shared';
import { useAiTextGeneration } from './scale-or-edit/creative';
import { useAiSenderGeneration } from './scale-or-edit/creative/useAiSenderGeneration';

interface Props {
  bulkForm: ReturnType<typeof useBulkEditPreparedDropsForm>;
  isBusy: boolean;
  countryKey: string;
  dropCount: number;
  editDropIds: string[];
  dropLabels?: DropLabel[];
  onAddSender?: ScaleDropModalProps['onAddSender'];
  onAddText?: ScaleDropModalProps['onAddText'];
  onDeleteTextsBulk?: ScaleDropModalProps['onDeleteTextsBulk'];
  onBulkSave?: ScaleDropModalProps['onBulkSave'];
  onClose?: () => void;
  onHideTextsAfterUseAndHide?: (texts: string[]) => Promise<void>;
  tagsByTextId?: Record<string, PerformanceTag[]>;
  tagColors?: Record<string, string>;
  smsTextsWithIds?: ScaleDropModalProps['smsTextsWithIds'];
}

const NOOP_ADD = async (_t: string[]) => {};

export function BulkEditCreativeColumn(props: Props) {
  const {
    bulkForm,
    isBusy,
    countryKey,
    dropCount,
    editDropIds: _editDropIds,
    dropLabels,
    onAddSender,
    onAddText,
    onBulkSave: _onBulkSave,
    onClose: _onClose,
    onDeleteTextsBulk,
    onHideTextsAfterUseAndHide,
    tagsByTextId,
    tagColors,
    smsTextsWithIds,
  } = props;
  const { hasPermission } = useAuth();
  const showAi = Boolean(onAddText) && hasPermission('campaign_texts.generate');
  const showAiSenders = Boolean(onAddSender) && hasPermission('campaign_senders.generate');

  const onSaveGeneratedPerDrop = useCallback(
    async (batches: string[][]) => {
      bulkForm.setPerDropSmsTexts(batches);
    },
    [bulkForm],
  );

  const ai = useAiTextGeneration({
    countryKey,
    onAddTexts: onAddText ?? NOOP_ADD,
    onSaveGeneratedAndSelectOnly: bulkForm.saveGeneratedAndSelectOnly,
    onMarkTextsForHide: bulkForm.markTextsForHide,
    onSaveGeneratedPerDrop,
    onAiGeneratedDeselectOld: bulkForm.clearTextsSelection,
    onAfterSave: () => {
      bulkForm.setShowTexts(false);
      bulkForm.setShowSenders(true);
    },
    onHideTextsAfterUseAndHide,
  });

  const onSaveGeneratedSendersPerDrop = useCallback(
    async (batches: string[][]) => {
      bulkForm.setPerDropSenderNames(batches);
    },
    [bulkForm],
  );

  const aiSenders = useAiSenderGeneration({
    countryKey,
    onAddSenders: onAddSender ? async (s) => { for (const n of s) await onAddSender(n); } : async () => {},
    onSaveGeneratedAndSelectOnly: bulkForm.saveGeneratedSendersAndSelectOnly,
    onMarkSendersForHide: bulkForm.markSendersForHide,
    onSaveGeneratedPerDrop: onSaveGeneratedSendersPerDrop,
    onPerDropSendersApplied: (batches) => bulkForm.setPerDropSenderNames(batches),
    onAiGeneratedDeselectOld: bulkForm.clearSendersSelection,
    onAfterSave: () => {
      bulkForm.setShowSenders(false);
      bulkForm.setShowTexts(true);
    },
  });

  const hasAiSenders = Boolean(
    (aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0) || aiSenders.generatedSenders.length > 0,
  );
  const hasAiTexts = Boolean(
    (ai.perDropBatches && ai.perDropBatches.length > 0) || ai.generatedTexts.length > 0,
  );
  const sendersTakeFullSpace = hasAiSenders || !bulkForm.showTexts;
  const textsTakeFullSpace = hasAiTexts || !bulkForm.showSenders;

  return (
    <>
      <ColumnHeader icon={MessageSquare} title="Creative" subtitle="Senders & Texts" />
      <div
        className={cn(
          'scale-creative-grid min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3',
          sendersTakeFullSpace && 'scale-creative-grid-ai-senders',
          textsTakeFullSpace && !sendersTakeFullSpace && 'scale-creative-grid-ai-texts',
          !bulkForm.showSenders && bulkForm.showTexts && 'scale-creative-grid-senders-collapsed',
          bulkForm.showSenders && !bulkForm.showTexts && 'scale-creative-grid-texts-collapsed',
        )}
      >
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <SendersPickerSection
            sendersList={bulkForm.sendersList}
            selectedSenders={bulkForm.selectedSenderNames}
            onToggleSender={bulkForm.toggleSender}
            onSelectAll={bulkForm.selectAllSenders}
            onClearSelection={bulkForm.clearSendersSelection}
            onDeleteSender={bulkForm.handleDeleteSender}
            onDeleteSenders={bulkForm.handleDeleteSenders}
            search={bulkForm.senderSearch}
            onSearch={bulkForm.setSenderSearch}
            newValue={bulkForm.newSenderValue}
            setNewValue={bulkForm.setNewSenderValue}
            onAdd={onAddSender ? bulkForm.handleAddSender : undefined}
            isBusy={isBusy}
            expanded={bulkForm.showSenders}
            onToggleExpand={() => bulkForm.setShowSenders((v: boolean) => !v)}
            templates={bulkForm.senderTemplates as SenderTemplateOption[]}
            activeTemplateId={bulkForm.activeTemplateId}
            onSelectTemplate={bulkForm.handleSelectTemplate}
            onCreateTemplate={bulkForm.handleSaveAsTemplate}
            canSaveTemplate={bulkForm.canSaveTemplate}
            pendingHideSenders={bulkForm.pendingHideSenders}
            replacedByAiSenders={bulkForm.replacedByAiSenders}
            actionSlot={
              showAiSenders ? (
                <AiGenerateSendersDropdown
                  countryKey={countryKey}
                  dropCount={dropCount}
                  dropLabels={dropLabels}
                  onGenerated={aiSenders.handleAiGenerated}
                />
              ) : undefined
            }
            toolbarSlot={
              showAiSenders ? (
                <AnimatePresence>
                  {aiSenders.perDropBatches && aiSenders.perDropBatches.length > 0 ? (
                    <GeneratedSendersPreviewPerDrop
                      batches={aiSenders.perDropBatches}
                      groupIdsPerDrop={aiSenders.groupIdsPerDrop}
                      dropLabels={dropLabels}
                      onSave={aiSenders.handleSavePerDrop}
                      onDismiss={aiSenders.handleDismissPerDrop}
                      onRegenerate={aiSenders.handleRegeneratePerDrop}
                      onRegenerateAll={aiSenders.handleRegenerateAllPerDrop}
                      onSaveAndHide={aiSenders.handleSaveAndHidePerDrop}
                      isRegenerating={aiSenders.isRegenerating}
                      useAndHideOnly={dropCount > 1}
                      applied={aiSenders.perDropApplied}
                      generationTimeMs={aiSenders.perDropGenerationTimeMs}
                      fillHeight
                    />
                  ) : aiSenders.generatedSenders.length > 0 ? (
                    <GeneratedSendersPreview
                      senders={aiSenders.generatedSenders}
                      onSave={aiSenders.handleSaveGenerated}
                      onSaveAndHide={aiSenders.handleSaveAndHide}
                      onDismiss={aiSenders.handleDismiss}
                      onRegenerate={aiSenders.handleRegenerate}
                      isRegenerating={aiSenders.isRegenerating}
                      fillHeight
                    />
                  ) : null}
                </AnimatePresence>
              ) : undefined
            }
          />
        </div>
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
          <TextsPickerSection
            textsList={bulkForm.textsList}
            selectedTexts={bulkForm.selectedSmsTexts}
            onToggleText={bulkForm.toggleText}
            onSelectAll={bulkForm.selectAllTexts}
            onClearSelection={bulkForm.clearTextsSelection}
            onDeleteText={bulkForm.handleDeleteText}
            onDeleteTextsBulk={onDeleteTextsBulk}
            search={bulkForm.textSearch}
            onSearch={bulkForm.setTextSearch}
            newValue={bulkForm.newTextValue}
            setNewValue={bulkForm.setNewTextValue}
            onAdd={onAddText ? bulkForm.handleAddText : undefined}
            isBusy={isBusy}
            expanded={bulkForm.showTexts}
            onToggleExpand={() => bulkForm.setShowTexts((v: boolean) => !v)}
            tagsByTextId={tagsByTextId}
            tagColors={tagColors}
            smsTextsWithIds={smsTextsWithIds}
            pendingHideTexts={bulkForm.pendingHideTexts}
            replacedByAiTexts={bulkForm.replacedByAiTexts}
            actionSlot={
              showAi ? (
                <AiGenerateDropdown
                  countryKey={countryKey}
                  dropCount={dropCount}
                  dropLabels={dropLabels}
                  existingBatches={
                    ai.perDropBatches?.length === dropCount ? ai.perDropBatches : undefined
                  }
                  onOpen={() => bulkForm.setShowSenders(false)}
                  onGenerated={ai.handleAiGenerated}
                />
              ) : undefined
            }
            toolbarSlot={
              showAi ? (
                <AnimatePresence>
                  {ai.perDropBatches && ai.perDropBatches.length > 0 ? (
                    <GeneratedTextsPreviewPerDrop
                      batches={ai.perDropBatches}
                      translationsPerDrop={ai.perDropTranslations}
                      groupIdsPerDrop={ai.groupIdsPerDrop}
                      onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                      isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                      dropLabels={dropLabels}
                      onSave={ai.handleSavePerDrop}
                      onDismiss={ai.handleDismissPerDrop}
                      onRegenerate={ai.handleRegeneratePerDrop}
                      onRegenerateAll={ai.handleRegenerateAllPerDrop}
                      onRegenerateOverLength={ai.handleRegenerateOverLengthPerDrop}
                      onRegenerateFailed={ai.handleRegenerateFailedPerDrop}
                      onSaveAndHide={ai.handleSaveAndHidePerDrop}
                      onTranslatePerDropAll={ai.handleTranslatePerDropAll}
                      onTranslatePerDropMissing={ai.handleTranslatePerDropMissing}
                      onTranslateAllPerDrop={ai.handleTranslateAllPerDrop}
                      isRegenerating={ai.isRegenerating}
                      isTranslating={ai.isTranslating}
                      useAndHideOnly={dropCount > 1}
                      applied={ai.perDropApplied}
                      generationTimeMs={ai.perDropGenerationTimeMs}
                    />
                  ) : ai.generatedTexts.length > 0 ? (
                    <GeneratedTextsPreview
                      texts={ai.generatedTexts}
                      translations={ai.generatedTranslations}
                      onDevCopyExactPrompt={ai.handleDevCopyExactModelPrompt}
                      isDevCopyExactPromptPending={ai.isDevCopyExactPromptPending}
                      onSave={ai.handleSaveGenerated}
                      onSaveAndHide={ai.handleSaveAndHide}
                      onDismiss={ai.handleDismiss}
                      onRegenerate={ai.handleRegenerate}
                      onRegenerateOverLength={ai.handleRegenerateOverLength}
                      onTranslateAll={ai.handleTranslateAll}
                      onTranslateMissing={ai.handleTranslateMissing}
                      isRegenerating={ai.isRegenerating}
                      isTranslating={ai.isTranslating}
                    />
                  ) : null}
                </AnimatePresence>
              ) : undefined
            }
          />
        </div>
      </div>
    </>
  );
}
